// Sugalarm — web command-station household status strip (cycle 170).
//
// WEB FRAME ONLY. One live tile per ACCESSIBLE member (the owned ∪ linked
// set the API already enforces — sourced from `members`, the same list
// the active-member switcher uses; never a member outside it). Each tile
// reads existing WebSocket state (per-member `byMember` live readings +
// the `dashboardCards` snapshot for staleness / alert level) — it adds NO
// new frames. Selecting a tile sets the viewed member via the existing
// active-member switch (the Dashboard then follows). Member-supplied
// names render as text (never innerHTML).

import { MemberAvatar } from "../ui/MemberAvatar";
import { TrendArrow } from "../glucose/TrendArrow";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import { useThresholds } from "../../hooks/useThresholds";
import { selectCurrentGlucoseFor } from "../../lib/currentGlucose";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneColor,
  zoneLabel,
  zoneOf,
} from "../../lib/glucose";
import type { Member } from "../../lib/types";
import styles from "./HouseholdStrip.module.css";

interface Props {
  members: Member[];
  socket: GlucoseSocketState;
  now: number;
  units: "mg/dL" | "mmol/L";
  activeMemberId: string | null;
  onSelect: (memberId: string) => void;
}

function providerLabel(provider: string | null): string {
  if (!provider) return "No CGM";
  if (provider === "dexcom") return "Dexcom";
  if (provider === "libre") return "Libre";
  return provider[0]!.toUpperCase() + provider.slice(1);
}

export function HouseholdStrip({
  members,
  socket,
  units,
  activeMemberId,
  onSelect,
}: Props) {
  const thresholds = useThresholds();
  const cardById = new Map(
    socket.dashboardCards.map((c) => [c.member_id, c]),
  );

  // Skip archived members (no archived_at) — defensive; the list is
  // already the accessible active set.
  const visible = members.filter((m) => !m.archived_at);

  return (
    <div className={styles.strip} role="list" aria-label="Household members">
      {visible.map((m) => {
        const card = cardById.get(m.id);
        // Cycle 433: the strip follows the same display rule — a number the
        // client cannot prove is current renders as the existing No Data
        // treatment.
        // ⚠ Cycle 435: PER MEMBER, through the ONE selector. Confirmation is
        // keyed on the member the reading belongs to — one member's frame
        // no longer proves another member's number — and the selector
        // samples `Date.now()` itself rather than the ticking `now` prop.
        const view = selectCurrentGlucoseFor(socket, m.id);
        const current = view.displayValue;
        const stale = current == null;
        const noData = view.reading == null;
        const zone = current ? zoneOf(current.sgv, thresholds) : "unknown";
        const color = stale ? "var(--ink-3)" : zoneColor(zone);
        const trend = current ? trendInfo(current.trend, current.trendRate) : null;
        const ageMin = view.ageMinutes;
        const alertLevel = card?.current_alert_level ?? null;
        const provider = m.cgm_provider ?? card?.cgm_provider ?? null;
        const selected = m.id === activeMemberId;

        return (
          <button
            type="button"
            role="listitem"
            key={m.id}
            className={`${styles.tile} ${selected ? styles.tileActive : ""}`}
            onClick={() => onSelect(m.id)}
            aria-pressed={selected}
          >
            <div className={styles.avatarWrap}>
              <MemberAvatar avatarId={m.avatar_id} name={m.display_name} size={36} />
              {alertLevel ? (
                <span
                  className={styles.alarmDot}
                  style={{ background: zoneColor(alertLevel) }}
                  aria-hidden
                />
              ) : null}
            </div>
            <div className={styles.body}>
              <div className={styles.name}>{m.display_name}</div>
              <div className={styles.readingRow}>
                <span className={`${styles.sgv} tnum`} style={{ color }}>
                  {current ? displayValue(current.sgv, units) : "—"}
                </span>
                {trend && !noData ? (
                  <TrendArrow
                    glyph={trend.glyph}
                    zone={stale ? "unknown" : zone}
                    stale={stale}
                    size="16px"
                  />
                ) : null}
              </div>
              {/* The status text reflects the CURRENT reading's zone (In
                  range / High / Low …) so it always matches the value above
                  it — not a lingering `current_alert_level` that can read
                  "High" after the reading has dropped back into range. The
                  avatar alarm dot still flags an active alert episode. */}
              <div className={styles.sub}>
                {noData ? (
                  <span>{providerLabel(provider)} · no data</span>
                ) : (
                  <>
                    <span style={{ color }}>
                      {stale ? "stale" : zoneLabel(zone)}
                    </span>
                    {!stale && ageMin != null ? (
                      <span> · {fmtAge(ageMin)}</span>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
